import { lazy } from 'react';

export interface ModuleCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'indigo';
  items: ModuleItem[];
}

export interface ModuleItem {
  label: string;
  path: string;
  description?: string;
  count?: number;
  badge?: string;
  permissions?: string[];
}

export interface ModuleStats {
  label: string;
  value: string | number;
  trend?: string;
  status?: 'success' | 'warning' | 'error' | 'info';
}

export interface ModuleConfig {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  path: string;
  icon: string;
  component: React.LazyExoticComponent<React.ComponentType<any>>; // Lazy loaded component
  landingComponent?: () => Promise<any>; // Lazy loaded landing page
  categories?: ModuleCategory[];
  stats?: ModuleStats[];
  permissions?: string[];
  dependencies?: string[];
  version?: string;
}

// Module configurations
export const modules: Record<string, ModuleConfig> = {
  'ui-examples': {
    id: 'ui-examples',
    name: 'UI Examples',
    description: 'Component library showcase and documentation',
    enabled: false,
    path: '/ui-examples',
    icon: 'palette',
    component: lazy(() => import('@textile-erp/ui/dist/examples').then(m => ({ default: m.UIShowcase }))),
    categories: [
      {
        id: 'core-ui',
        title: 'Core UI Components',
        description: 'Essential building blocks for user interfaces',
        icon: 'Package',
        color: 'blue',
        items: [
          { label: 'Buttons', path: '/ui-examples/buttons', description: 'Interactive buttons with variants, sizes, and states' },
          { label: 'Cards', path: '/ui-examples/cards', description: 'Content containers and information display' }
        ]
      },
      {
        id: 'data-display',
        title: 'Data Display & Lists',
        description: 'Components for displaying and managing large datasets',
        icon: 'BarChart',
        color: 'green',
        items: [
          { label: 'Data Tables', path: '/ui-examples/tables', description: 'Advanced tables with filtering, sorting, and bulk actions' },
          { label: 'Virtual Table', path: '/ui-examples/virtual-table', description: 'High-performance virtual scrolling for large datasets', badge: 'NEW' },
          { label: 'Table Grouping', path: '/ui-examples/virtual-table-grouping', description: 'Multi-level grouping with summaries and aggregation', badge: 'NEW' },
          { label: 'Virtual List', path: '/ui-examples/virtual-list', description: 'Efficiently render large lists with virtual scrolling' },
          { label: 'Infinite Scroll', path: '/ui-examples/infinite-scroll', description: 'Seamlessly load content as users scroll' },
          { label: 'Pagination + Virtual', path: '/ui-examples/pagination-virtual', description: 'Combine pagination UX with virtual scrolling performance' }
        ]
      },
      {
        id: 'forms-inputs',
        title: 'Forms & Inputs',
        description: 'Form components and input controls',
        icon: 'FileText',
        color: 'purple',
        items: [
          { label: 'Basic Forms', path: '/ui-examples/forms', description: 'Input fields, validation, and form layouts' },
          { label: 'Adaptive Forms', path: '/ui-examples/adaptive-forms', description: 'Single reusable form with multiple layouts and auto-density' },
          { label: 'Date Pickers', path: '/ui-examples/datepicker', description: 'Date, time, and datetime pickers with theme support' }
        ]
      },
      {
        id: 'feedback-overlays',
        title: 'Feedback & Overlays',
        description: 'User feedback and overlay interfaces',
        icon: 'MessageCircle',
        color: 'orange',
        items: [
          { label: 'Modals', path: '/ui-examples/modals', description: 'Dialog boxes, wizards, and overlay interfaces' },
          { label: 'Loading States', path: '/ui-examples/loading', description: 'Spinners, skeletons, and loading indicators' },
          { label: 'Alerts & Toasts', path: '/ui-examples/alerts', description: 'Alert messages, toast notifications, and feedback components' },
          { label: 'Error Boundaries', path: '/ui-examples/error-boundaries', description: 'Error handling, monitoring, and recovery components' }
        ]
      },
      {
        id: 'layouts',
        title: 'Layout & Structure',
        description: 'Page layouts and structural components',
        icon: 'Layout',
        color: 'indigo',
        items: [
          { label: 'Page Layouts', path: '/ui-examples/layouts', description: 'Complete page layout patterns and containers' },
          { label: 'Tab Layouts', path: '/ui-examples/layouts', description: 'Tabbed interfaces and content organization' },
          { label: 'Module Landing', path: '/ui-examples/layouts', description: 'Category-based navigation patterns' },
          { label: 'Compact Landing', path: '/ui-examples/module-landing-compact', description: 'Space-efficient dynamic column layout for module pages', badge: 'NEW' }
        ]
      },
      {
        id: 'theming',
        title: 'Theming & Customization',
        description: 'Visual themes and customization options',
        icon: 'Palette',
        color: 'red',
        items: [
          { label: 'Theme System', path: '/ui-examples/themes', description: '6 built-in color presets with light/dark mode support' },
          { label: 'Color Customization', path: '/ui-examples/themes', description: 'Custom background colors and theme variables' }
        ]
      }
    ],
    stats: [
      { label: 'Total Components', value: 14, status: 'success' },
      { label: 'Virtual Scrolling', value: 4, status: 'success', trend: 'NEW' },
      { label: 'Categories', value: 6, status: 'info' },
      { label: 'Theme Presets', value: 6, status: 'success' }
    ],
    permissions: [],
    dependencies: ['core'],
    version: '1.0.0'
  },
  
  dashboard: {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Overview and key metrics',
    enabled: false,
    path: '/',
    icon: 'Home',
    component: lazy(() => import('../app/wrappers/DashboardWrapper').then(m => ({ default: m.DashboardWrapper }))),
    permissions: ['dashboard.view'],
    version: '1.0.0'
  },

  payroll: {
    id: 'payroll',
    name: 'Payroll Management',
    description: 'Manage payroll, departments, and employee compensation',
    enabled: true,
    path: '/payroll',
    icon: 'DollarSign',
    component: lazy(() => import('../app/wrappers/PayrollWrapper').then(m => ({ default: m.PayrollWrapper }))),
    stats: [
      { label: 'Total Departments', value: 0, trend: 'NEW', status: 'info' },
      { label: 'Active Employees', value: 0, status: 'info' },
      { label: 'This Month Payroll', value: '$0', trend: '-', status: 'success' },
      { label: 'Pending Payments', value: 0, status: 'warning' }
    ],
    permissions: ['payroll.view'],
    dependencies: ['core'],
    version: '1.0.0'
  },

  users: {
    id: 'users',
    name: 'User & Access Management',
    description: 'Manage users, roles, and permissions',
    enabled: true,
    path: '/users',
    icon: 'Users',
    component: lazy(() => import('../app/wrappers/UserManagementWrapper').then(m => ({ default: m.UserManagementWrapper }))),
    stats: [
      { label: 'Total Users', value: 1247, trend: '+12%', status: 'success' },
      { label: 'Active Users', value: 892, trend: '+5%', status: 'success' },
      { label: 'Pending Approvals', value: 23, status: 'warning' },
      { label: 'Blocked Users', value: 8, status: 'error' }
    ],
    permissions: ['users.view'],
    dependencies: ['core'],
    version: '1.0.0'
  },

  // Merged into User & Access Management: Role Management now lives at
  // /users/roles, and Policy Management has had no landing card since
  // "removed unwanted screens". Disabled so the sidebar shows one entry; the
  // routes/menus still exist, so flipping this back restores the old module.
  identity: {
    id: 'identity',
    name: 'Identity & Access',
    description: 'Identity and access management',
    enabled: false,
    path: '/identity',
    icon: 'Shield',
    component: lazy(() => import('../app/wrappers/IdentityManagementWrapper').then(m => ({ default: m.IdentityManagementWrapper }))),
    stats: [
      { label: 'Active Roles', value: 45, trend: '+3', status: 'success' },
      { label: 'Policies', value: 127, trend: '+8', status: 'success' },
      { label: 'Compliance Score', value: '94%', trend: '+2%', status: 'success' },
      { label: 'Risk Issues', value: 3, status: 'warning' }
    ],
    permissions: ['identity.view'],
    dependencies: ['core'],
    version: '1.0.0'
  },

  // Future modules (disabled for now)
  inventory: {
    id: 'inventory',
    name: 'Inventory Management',
    description: 'Manage stock, products, and warehouses',
    enabled: false, // Will be enabled when module is ready
    path: '/inventory',
    icon: 'Package',
    component: lazy(() => import('../app/ModulePlaceholder').then(m => ({ default: () => m.ModulePlaceholder({ moduleName: 'Inventory Management' }) }))),
    categories: [
      {
        id: 'masters',
        title: 'Masters',
        description: 'Inventory master data',
        icon: 'Database',
        color: 'blue',
        items: [
          { label: 'Products', path: '/inventory/products', description: 'Manage product catalog and details' },
          { label: 'Categories', path: '/inventory/categories', description: 'Organize products by categories' },
          { label: 'Warehouses', path: '/inventory/warehouses', description: 'Manage warehouse locations' },
          { label: 'Suppliers', path: '/inventory/suppliers', description: 'Supplier management and contacts' }
        ]
      },
      {
        id: 'process',
        title: 'Process',
        description: 'Inventory operations',
        icon: 'Activity',
        color: 'green',
        items: [
          { label: 'Stock Entry', path: '/inventory/entry', description: 'Record incoming stock' },
          { label: 'Stock Transfer', path: '/inventory/transfer', description: 'Transfer between warehouses' },
          { label: 'Stock Adjustment', path: '/inventory/adjustment', description: 'Adjust stock quantities' },
          { label: 'Physical Count', path: '/inventory/count', description: 'Physical inventory counting' }
        ]
      },
      {
        id: 'reports',
        title: 'Reports',
        description: 'Inventory analytics and reports',
        icon: 'BarChart',
        color: 'purple',
        items: [
          { label: 'Stock Summary', path: '/inventory/reports/stock', description: 'Current stock levels and values' },
          { label: 'Movement Analysis', path: '/inventory/reports/movement', description: 'Track stock movements and trends' },
          { label: 'Aging Report', path: '/inventory/reports/aging', description: 'Identify slow-moving stock' },
          { label: 'Reorder Report', path: '/inventory/reports/reorder', description: 'Items that need reordering' }
        ]
      }
    ],
    stats: [
      { label: 'Total Products', value: 2547, trend: '+125', status: 'success' },
      { label: 'Stock Value', value: '$2.4M', trend: '+8%', status: 'success' },
      { label: 'Low Stock Items', value: 23, status: 'warning' },
      { label: 'Out of Stock', value: 5, status: 'error' }
    ],
    permissions: ['inventory.view'],
    dependencies: ['core'],
    version: '1.0.0'
  },

  sales: {
    id: 'sales',
    name: 'Sales Management',
    description: 'Manage orders, customers, and sales processes',
    enabled: false,
    path: '/sales',
    icon: 'TrendingUp',
    component: lazy(() => import('../app/ModulePlaceholder').then(m => ({ default: () => m.ModulePlaceholder({ moduleName: 'Sales Management' }) }))),
    permissions: ['sales.view'],
    dependencies: ['core', 'inventory'],
    version: '1.0.0'
  },

  parking: {
    id: 'parking',
    name: 'Parking Management',
    description: 'Manage parking areas and parking operations',
    enabled: true,
    path: '/parking',
    icon: 'Package',
    component: lazy(() => import('../app/wrappers/ParkingWrapper').then(m => ({ default: m.ParkingWrapper }))),
    stats: [
      { label: 'Total Parking Areas', value: 0, trend: 'NEW', status: 'info' },
      { label: 'Active Areas', value: 0, status: 'info' },
      { label: 'Occupied Spaces', value: 0, trend: '-', status: 'success' },
      { label: 'Available Spaces', value: 0, status: 'success' }
    ],
    permissions: ['parking.view'],
    dependencies: ['core'],
    version: '1.0.0'
  },

  visitors: {
    id: 'visitors',
    name: 'Visitors Dashboard',
    description: 'Visitors overview and activity',
    enabled: true,
    path: '/visitors',
    icon: 'Users',
    component: lazy(() => import('../app/wrappers/VisitorsWrapper').then(m => ({ default: m.VisitorsWrapper }))),
    permissions: ['visitors.view'],
    dependencies: ['core'],
    version: '1.0.0'
  },

  monitoring: {
    id: 'monitoring',
    name: 'Monitoring & Logs',
    description: 'Auth logs, audit trail, and sync activity',
    enabled: false,
    path: '/monitoring',
    icon: 'Activity',
    component: lazy(() => import('../app/wrappers/MonitoringWrapper').then(m => ({ default: m.MonitoringWrapper }))),
    permissions: ['monitoring.view'],
    dependencies: ['core'],
    version: '1.0.0'
  },

  purchase: {
    id: 'purchase',
    name: 'Purchase Management',
    description: 'Manage suppliers, agents, and purchase masters',
    enabled: true,
    path: '/purchase',
    icon: 'ShoppingCart',
    component: lazy(() => import('../app/wrappers/PurchaseWrapper').then(m => ({ default: m.PurchaseWrapper }))),
    categories: [
      {
        id: 'masters',
        title: 'Masters',
        description: 'Purchase master data',
        icon: 'Database',
        color: 'blue',
        items: [
          { label: 'City', path: '/purchase/cities', description: 'Manage purchase cities' },
          { label: 'Market', path: '/purchase/markets', description: 'Manage purchase markets' },
          { label: 'Order Type', path: '/purchase/operation-modes', description: 'Manage order types' },
          { label: 'Grade', path: '/purchase/supplier-grades', description: 'Manage supplier grades' },
          { label: 'Agent', path: '/purchase/agents', description: 'Manage purchase agents' },
          { label: 'Supplier', path: '/purchase/suppliers', description: 'Manage suppliers' },
        ]
      },
      {
        id: 'reports',
        title: 'Reports',
        description: 'Purchase reports and logs',
        icon: 'FileText',
        color: 'purple',
        items: [
          // Hidden from the UI — login events now live under User & Access
          // Management → Reports → Auth Logs. See the matching comment in
          // PurchaseWrapper.tsx; uncomment both to restore the card.
          // { label: 'Auth Logs', path: '/purchase/auth-logs', description: 'Login and logout events across all platforms' },
          { label: 'Audit Logs', path: '/purchase/audit-logs', description: 'Create, update and delete changes on purchase records' },
          { label: 'Sync History', path: '/purchase/sync-history', description: 'Mobile device synchronisation events' },
        ]
      }
    ],
    permissions: ['purchase.view'],
    dependencies: ['core'],
    version: '1.0.0'
  }
};

// Helper functions
export function getEnabledModules(): ModuleConfig[] {
  return Object.values(modules).filter(module => module.enabled);
}

export function getModuleById(id: string): ModuleConfig | undefined {
  return modules[id];
}

export function getModulesByPermission(userPermissions: string[]): ModuleConfig[] {
  return getEnabledModules().filter(module => 
    !module.permissions || 
    module.permissions.some(perm => userPermissions.includes(perm))
  );
}

export function getModuleNavigation() {
  return getEnabledModules().map(module => ({
    id: module.id,
    name: module.name,
    path: module.path,
    icon: module.icon,
    description: module.description
  }));
}